import { API_URL } from '../config/api';

/**
 * Token data structure returned from API
 */
export interface ApiTokenData {
  access_token: string;
  refresh_token: string;
  expires_in: number; // seconds until token expires
  token_type: string;
}

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Token info with expiration tracking
 */
export interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // timestamp in milliseconds
  expiresIn: number; // seconds remaining
  isExpired: boolean;
  willExpireSoon: boolean; // expires within 5 minutes
}

/**
 * API Token Manager
 * Handles authentication tokens from /api/v1/users/login and /api/v1/users/refresh
 * Performs client-side token expiration validation
 */
class ApiTokenManager {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiresAt: number | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;

  // Storage keys
  private readonly ACCESS_TOKEN_KEY = 'api_access_token';
  private readonly REFRESH_TOKEN_KEY = 'api_refresh_token';
  private readonly EXPIRES_AT_KEY = 'api_token_expires_at';

  constructor() {
    this.loadTokensFromStorage();
    this.setupAutomaticRefresh();
  }

  /**
   * Login with email and password
   * Returns token data from /api/v1/users/login
   */
  async login(credentials: LoginCredentials): Promise<ApiTokenData> {
    try {
      console.log('🔐 API Token Manager: Logging in...');
      
      const response = await fetch(`${API_URL}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Login failed: ${response.statusText}`);
      }

      const tokenData: ApiTokenData = await response.json();
      
      // Store tokens
      this.setTokens(tokenData);
      
      console.log('✅ API Token Manager: Login successful');
      return tokenData;
    } catch (error) {
      console.error('❌ API Token Manager: Login failed', error);
      throw error;
    }
  }

  /**
   * Refresh the access token using the refresh token
   * Calls /api/v1/users/refresh
   */
  async refresh(): Promise<boolean> {
    // Prevent concurrent refresh requests
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.refreshToken) {
      return false;
    }

    this.isRefreshing = true;
    console.log('🔄 API Token Manager: Refreshing token...');

    this.refreshPromise = new Promise(async (resolve) => {
      try {
        const response = await fetch(`${API_URL}/users/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.refreshToken}`,
          },
          body: JSON.stringify({
            refresh_token: this.refreshToken,
          }),
        });

        if (!response.ok) {
          console.error('❌ API Token Manager: Refresh failed', response.status);
          
          // If refresh fails with 401, clear tokens and logout
          if (response.status === 401) {
            this.clearTokens();
          }
          
          resolve(false);
          return;
        }

        const tokenData: ApiTokenData = await response.json();
        
        // Update tokens
        this.setTokens(tokenData);
        
        console.log('✅ API Token Manager: Token refreshed successfully');
        resolve(true);
      } catch (error) {
        console.error('❌ API Token Manager: Refresh error', error);
        resolve(false);
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    });

    return this.refreshPromise;
  }

  /**
   * Set tokens and calculate expiration time
   */
  private setTokens(tokenData: ApiTokenData): void {
    this.accessToken = tokenData.access_token;
    this.refreshToken = tokenData.refresh_token;
    
    // Calculate expiration timestamp (current time + expires_in)
    const now = Date.now();
    this.tokenExpiresAt = now + (tokenData.expires_in * 1000);

    // Store in localStorage for persistence
    localStorage.setItem(this.ACCESS_TOKEN_KEY, this.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, this.refreshToken);
    localStorage.setItem(this.EXPIRES_AT_KEY, this.tokenExpiresAt.toString());

    // Setup automatic refresh
    this.scheduleTokenRefresh();
  }

  /**
   * Load tokens from localStorage
   */
  private loadTokensFromStorage(): void {
    try {
      this.accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
      this.refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);
      
      if (expiresAt) {
        this.tokenExpiresAt = parseInt(expiresAt, 10);
      }

      // Check if token is expired
      if (this.accessToken && this.isTokenExpired()) {
        console.log('🔄 API Token Manager: Token expired on load, will refresh');
        // Don't clear yet - let the refresh mechanism handle it
      }
    } catch (error) {
      console.error('❌ API Token Manager: Error loading tokens from storage', error);
      this.clearTokens();
    }
  }

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = null;

    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    console.log('🗑️ API Token Manager: Tokens cleared');
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Get current refresh token
   */
  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  /**
   * Check if token is expired (client-side validation)
   */
  isTokenExpired(): boolean {
    if (!this.tokenExpiresAt) {
      return true;
    }

    const now = Date.now();
    return now >= this.tokenExpiresAt;
  }

  /**
   * Check if token will expire soon (within 5 minutes)
   */
  willTokenExpireSoon(): boolean {
    if (!this.tokenExpiresAt) {
      return true;
    }

    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    return (this.tokenExpiresAt - now) <= fiveMinutes;
  }

  /**
   * Get token info with expiration details
   */
  getTokenInfo(): TokenInfo | null {
    if (!this.accessToken || !this.tokenExpiresAt) {
      return null;
    }

    const now = Date.now();
    const expiresIn = Math.max(0, Math.floor((this.tokenExpiresAt - now) / 1000));

    return {
      accessToken: this.accessToken,
      refreshToken: this.refreshToken || '',
      expiresAt: this.tokenExpiresAt,
      expiresIn,
      isExpired: this.isTokenExpired(),
      willExpireSoon: this.willTokenExpireSoon(),
    };
  }

  /**
   * Check if user is authenticated (has valid tokens)
   */
  isAuthenticated(): boolean {
    return !!this.accessToken && !!this.refreshToken && !this.isTokenExpired();
  }

  /**
   * Setup automatic token refresh
   */
  private setupAutomaticRefresh(): void {
    // Check token status every minute
    setInterval(() => {
      if (this.willTokenExpireSoon() && !this.isRefreshing) {
        console.log('⏰ API Token Manager: Token expiring soon, refreshing proactively...');
        this.refresh();
      }
    }, 60000); // Check every 1 minute
  }

  /**
   * Schedule token refresh before expiration
   */
  private scheduleTokenRefresh(): void {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.tokenExpiresAt) {
      return;
    }

    const now = Date.now();
    const timeUntilExpiry = this.tokenExpiresAt - now;
    
    // Refresh 5 minutes before expiry, or in 1 minute if less time remains
    const refreshTime = Math.max(timeUntilExpiry - (5 * 60 * 1000), 60000);

    console.log(`⏰ API Token Manager: Token expires in ${Math.floor(timeUntilExpiry / 1000)}s, will refresh in ${Math.floor(refreshTime / 1000)}s`);

    this.refreshTimer = setTimeout(() => {
      console.log('⏰ API Token Manager: Scheduled refresh triggered');
      this.refresh();
    }, refreshTime);
  }

  /**
   * Ensure token is valid before making a request
   * Automatically refreshes if needed
   */
  async ensureValidToken(): Promise<string | null> {
    // If no token, can't proceed
    if (!this.accessToken) {
      console.log('❌ API Token Manager: No access token available');
      return null;
    }

    // If token is expired, refresh it
    if (this.isTokenExpired()) {
      console.log('🔄 API Token Manager: Token expired, refreshing...');
      const refreshed = await this.refresh();
      
      if (!refreshed) {
        console.error('❌ API Token Manager: Failed to refresh expired token');
        return null;
      }
    }
    // If token will expire soon, proactively refresh
    else if (this.willTokenExpireSoon() && !this.isRefreshing) {
      console.log('🔄 API Token Manager: Token expiring soon, proactively refreshing...');
      // Don't await - let it happen in background
      this.refresh().catch(err => {
        console.error('❌ API Token Manager: Background refresh failed', err);
      });
    }

    return this.accessToken;
  }

  /**
   * Logout - clear all tokens
   */
  logout(): void {
    console.log('🚪 API Token Manager: Logging out...');
    this.clearTokens();
  }
}

// Create singleton instance
export const apiTokenManager = new ApiTokenManager();

// Export for debugging
(window as any).apiTokenManager = apiTokenManager;

export default apiTokenManager;

